import { ref, type ComputedRef, type Ref } from 'vue';
import { api } from '../lib/api';
import type { EntityDocument, EntitySummary, EntityTemplate, TiptapDoc } from '../types/entities';
import type { JsonImportEntity, JsonImportPayload, JsonImportScene, JsonImportSegment } from '../components/JsonImportDialog.vue';

type ImportKind = 'chapter' | 'scene' | 'character' | 'location';

function importDocument(content?: string): TiptapDoc {
  const lines = content?.trim().split(/\r?\n/) ?? [];
  return { type: 'doc', content: lines.map((line) => ({ type: 'paragraph', ...(line ? { content: [{ type: 'text', text: line }] } : {}) })) };
}

function importTitleKey(value: string) { return value.trim().toLocaleLowerCase('de-DE'); }

export function useJsonImport(options: {
  workspaceId: ComputedRef<string | null>;
  templates: Ref<EntityTemplate[]>;
  entities: Ref<EntitySummary[]>;
  refresh: () => Promise<unknown>;
}) {
  const jsonImportOpen = ref(false);
  const jsonImportBusy = ref(false);
  const jsonImportError = ref('');
  const jsonImportResult = ref('');

  function openJsonImport() {
    jsonImportError.value = '';
    jsonImportResult.value = '';
    jsonImportOpen.value = true;
  }

  function importTemplate(kind: ImportKind) {
    const key = kind === 'chapter' ? 'kapitel' : kind === 'scene' ? 'szene' : kind === 'character' ? 'charakter' : 'ort';
    return options.templates.value.find((template) => template.status === 'active' && template.key === key)
      ?? (kind === 'location' ? options.templates.value.find((template) => template.status === 'active' && template.category === 'orte') : undefined);
  }

  async function importEntity(kind: ImportKind, input: JsonImportEntity, fields = input.fields ?? {}) {
    const workspaceId = options.workspaceId.value;
    if (!workspaceId) throw new Error('Kein Workspace geöffnet.');
    const template = importTemplate(kind);
    if (!template) throw new Error(kind === 'location' ? 'Für Orte existiert noch kein aktives Ort-Template. Lege zuerst unter „Struktur verwalten“ eines an.' : `Das System-Template für ${kind} fehlt.`);
    return api<EntityDocument>(`/api/v1/workspaces/${workspaceId}/entities`, {
      method: 'POST',
      body: JSON.stringify({ templateId: template.id, title: input.title.trim(), summary: input.summary?.trim() || null, contentDoc: importDocument(input.content ?? input.text), fieldValues: fields })
    });
  }

  async function importSegment(sceneId: string, input: JsonImportSegment) {
    const segment = await api<{ id: string }>(`/api/v1/entities/${sceneId}/scene-segments`, {
      method: 'POST', body: JSON.stringify({ label: input.title.trim(), contentDoc: importDocument(input.content ?? input.text) })
    });
    if (input.fields && Object.keys(input.fields).length) await api(`/api/v1/scene-segments/${segment.id}`, { method: 'PATCH', body: JSON.stringify({ fieldValues: input.fields }) });
  }

  async function importScene(input: JsonImportScene, sceneIdsByTitle: Map<string, string>) {
    const scene = await importEntity('scene', input);
    sceneIdsByTitle.set(importTitleKey(scene.title), scene.id);
    for (const segment of input.segments ?? []) await importSegment(scene.id, segment);
    return scene;
  }

  async function submitJsonImport(payload: JsonImportPayload) {
    jsonImportBusy.value = true;
    jsonImportError.value = '';
    jsonImportResult.value = '';
    try {
      const sceneIdsByTitle = new Map(options.entities.value.filter((entity) => entity.status === 'active' && entity.templateCategory === 'szene').map((entity) => [importTitleKey(entity.title), entity.id]));
      const created = { chapters: 0, scenes: 0, segments: 0, characters: 0, locations: 0 };
      for (const chapterInput of payload.chapters ?? []) {
        const sceneIds: string[] = [];
        for (const sceneInput of chapterInput.scenes ?? []) {
          const scene = await importScene(sceneInput, sceneIdsByTitle);
          sceneIds.push(scene.id); created.scenes += 1; created.segments += sceneInput.segments?.length ?? 0;
        }
        await importEntity('chapter', chapterInput, { ...(chapterInput.fields ?? {}), scenes: sceneIds });
        created.chapters += 1;
      }
      for (const sceneInput of payload.scenes ?? []) {
        await importScene(sceneInput, sceneIdsByTitle);
        created.scenes += 1; created.segments += sceneInput.segments?.length ?? 0;
      }
      for (const segmentInput of payload.segments ?? []) {
        const sceneId = sceneIdsByTitle.get(importTitleKey(segmentInput.scene ?? ''));
        if (!sceneId) throw new Error(`Segment „${segmentInput.title}“ verweist auf die unbekannte Szene „${segmentInput.scene}“.`);
        await importSegment(sceneId, segmentInput); created.segments += 1;
      }
      for (const characterInput of payload.characters ?? []) { await importEntity('character', characterInput); created.characters += 1; }
      for (const locationInput of payload.locations ?? []) { await importEntity('location', locationInput); created.locations += 1; }
      await options.refresh();
      jsonImportResult.value = [
        created.chapters && `${created.chapters} Kapitel`, created.scenes && `${created.scenes} Szenen`, created.segments && `${created.segments} Segmente`, created.characters && `${created.characters} Charaktere`, created.locations && `${created.locations} Orte`
      ].filter(Boolean).join(', ') || 'Keine neuen Einträge im JSON.';
    } catch (reason) {
      jsonImportError.value = reason instanceof Error ? reason.message : 'Der JSON-Import konnte nicht abgeschlossen werden.';
    } finally {
      jsonImportBusy.value = false;
    }
  }

  return { jsonImportOpen, jsonImportBusy, jsonImportError, jsonImportResult, openJsonImport, submitJsonImport };
}
